import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { articleService, IArticle } from '../../services/articleService';
import AnimatedTitle from '../../components/AnimatedTitle';
import ArticleImage from '../../components/common/ArticleImage';
import { authorName } from '../../components/editorial/StoryCard';

const HomePage: React.FC = () => {
  const storiesRef = useRef<HTMLDivElement>(null);
  const [activeArticle, setActiveArticle] = useState(0);
  const [articles, setArticles] = useState<IArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const fetchArticles = async () => {
    setLoading(true); setError(false);
    try { setArticles((await articleService.getAll()).slice(0, 5)); }
    catch { setError(true); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchArticles(); }, []);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const stories = Array.from(storiesRef.current?.querySelectorAll('.gallery-story') || []);
      const center = (window.innerHeight + 64) / 2;
      let active = 0;
      let nearest = Infinity;
      stories.forEach((story, index) => {
        const bounds = (story.querySelector('.gallery-image') || story).getBoundingClientRect();
        const distance = Math.abs(bounds.top + bounds.height / 2 - center);
        if (bounds.top <= center && bounds.bottom >= center && distance < nearest) {
          nearest = distance;
          active = index + 1;
        }
      });
      setActiveArticle(active);
    };
    const queueUpdate = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', queueUpdate, { passive: true });
    window.addEventListener('resize', queueUpdate);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', queueUpdate);
      window.removeEventListener('resize', queueUpdate);
    };
  }, [articles]);
  return <div className="gallery-home">
    <div className="gallery-intro"><AnimatedTitle showDefinition={false} /></div>
    <div className={`gallery-counter${activeArticle > 0 ? ' is-visible' : ''}`} aria-hidden={activeArticle === 0} aria-label={`Article ${activeArticle} of ${articles.length}`}>
      <span>{String(articles.length).padStart(3, '0')}</span>
      <span className="gallery-counter-window" aria-hidden="true"><span className="gallery-counter-reel" style={{ transform: `translateY(-${Math.max(0, activeArticle - 1) * 1.5}em)` }}>{articles.map((article, index) => <span key={article.id}>{String(index + 1).padStart(3, '0')}</span>)}</span></span>
    </div>
    <div ref={storiesRef} className="gallery-stories" aria-label="Latest articles">
      {loading ? <div className="gallery-status" role="status">Loading stories…</div> : error ? <div className="gallery-status"><p>Unable to load stories right now.</p><button className="editorial-button" onClick={fetchArticles}>Try again ↗</button></div> : articles.length ? articles.map((article, index) => (
        <section className="gallery-story" key={article.id} aria-label={article.title}>
          <Link to={`/article/${article.slug || article.id}`} className="gallery-story-link">
            <div className="gallery-image">{article.imageUrl ? <ArticleImage src={article.imageUrl} alt={article.title} loading={index === 0 ? 'eager' : 'lazy'} /> : <div className="story-no-image"><span>{article.category}</span><span>H.</span></div>}</div>
            <div className="gallery-caption"><span>{article.category}</span><h2>{article.title}</h2><span className="gallery-author">By {authorName(article)}</span></div>
          </Link>
        </section>
      )) : <div className="gallery-status">New stories are on their way.</div>}
    </div>
    <nav className="gallery-actions" aria-label="Get involved">
      <Link to="/authors" className="gallery-authors-link">Authors</Link>
      <Link to="/submit" className="gallery-contribute-link">Contribute</Link>
    </nav>
  </div>;
};
export default HomePage;
